import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Search,
  Eye,
  EyeOff,
  X,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  description: string;
  image_url: string;
  image_url2?: string;
  image_url3?: string;
  image_url4?: string;
  category_id: string;
  is_premium: boolean;
  is_visible: boolean;
  display_order: number;
  created_at: string;
  categories?: { name: string };
}

interface Category {
  id: string;
  name: string;
  description: string;
}

const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    image_url2: '',
    image_url3: '',
    image_url4: '',
    category_id: '',
    is_premium: false,
    is_visible: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        supabase
          .from('products')
          .select(`
            *,
            categories (
              name
            )
          `)
          .order('display_order', { ascending: true }),
        supabase
          .from('categories')
          .select('*')
          .order('name'),
      ]);

      if (productsRes.data) setProducts(productsRes.data);
      if (categoriesRes.data) setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || product.category_id === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => a.display_order - b.display_order);

  const openModal = (product?: Product) => {
    if (!product && categories.length === 0) {
      alert('Primero debes crear al menos una categoría antes de agregar productos.');
      return;
    }

    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        image_url: product.image_url,
        image_url2: product.image_url2 || '',
        image_url3: product.image_url3 || '',
        image_url4: product.image_url4 || '',
        category_id: product.category_id,
        is_premium: product.is_premium,
        is_visible: product.is_visible,
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        image_url2: '',
        image_url3: '',
        image_url4: '',
        category_id: '',
        is_premium: false,
        is_visible: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Calcular el display_order para el nuevo producto
      const maxOrder = products.length > 0 ? Math.max(...products.map(p => p.display_order || 0)) : 0;

      const productData = {
        name: formData.name,
        description: formData.description,
        image_url: formData.image_url,
        image_url2: formData.image_url2 || null,
        image_url3: formData.image_url3 || null,
        image_url4: formData.image_url4 || null,
        category_id: formData.category_id,
        is_premium: formData.is_premium,
        is_visible: formData.is_visible,
        display_order: maxOrder + 10, // Agregar 10 para dejar espacio para reordenar
      };

      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('products')
          .insert(productData);

        if (error) throw error;
      }

      await loadData();
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error al guardar el producto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (productId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este producto?')) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      await loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error al eliminar el producto');
    }
  };

  const toggleVisibility = async (product: Product) => {
    try {
      const { error } = await supabase
        .from('products')
        .update({ is_visible: !product.is_visible })
        .eq('id', product.id);

      if (error) throw error;

      await loadData();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  // Funciones para reordenar productos
  const getProductIndex = (productId: string) => {
    return filteredProducts.findIndex(p => p.id === productId);
  };

  const swapProducts = async (index1: number, index2: number) => {
    if (index1 < 0 || index2 < 0 || index1 >= filteredProducts.length || index2 >= filteredProducts.length) {
      return;
    }

    const product1 = filteredProducts[index1];
    const product2 = filteredProducts[index2];

    // Intercambiar display_order
    const tempOrder = product1.display_order;
    product1.display_order = product2.display_order;
    product2.display_order = tempOrder;

    try {
      // Actualizar en la base de datos
      await Promise.all([
        supabase
          .from('products')
          .update({ display_order: product1.display_order })
          .eq('id', product1.id),
        supabase
          .from('products')
          .update({ display_order: product2.display_order })
          .eq('id', product2.id)
      ]);

      // Recargar datos
      await loadData();
    } catch (error) {
      console.error('Error swapping products:', error);
      alert('Error al reordenar productos');
    }
  };

  const moveProduct = async (productId: string, direction: 'up' | 'down' | 'left' | 'right') => {
    const currentIndex = getProductIndex(productId);
    if (currentIndex === -1) return;

    const totalProducts = filteredProducts.length;
    const columns = 3; // Grid de 3 columnas en desktop
    let targetIndex = currentIndex;

    switch (direction) {
      case 'up':
        targetIndex = currentIndex - columns;
        break;
      case 'down':
        targetIndex = currentIndex + columns;
        break;
      case 'left':
        targetIndex = currentIndex - 1;
        // No permitir mover a la izquierda si está en la primera columna
        if (currentIndex % columns === 0) return;
        break;
      case 'right':
        targetIndex = currentIndex + 1;
        // No permitir mover a la derecha si está en la última columna
        if ((currentIndex + 1) % columns === 0) return;
        break;
    }

    if (targetIndex >= 0 && targetIndex < totalProducts) {
      await swapProducts(currentIndex, targetIndex);
    }
  };

  const canMoveProduct = (productId: string, direction: 'up' | 'down' | 'left' | 'right'): boolean => {
    const currentIndex = getProductIndex(productId);
    if (currentIndex === -1) return false;

    const totalProducts = filteredProducts.length;
    const columns = 3; // Grid de 3 columnas en desktop

    switch (direction) {
      case 'up':
        return currentIndex - columns >= 0;
      case 'down':
        return currentIndex + columns < totalProducts;
      case 'left':
        return currentIndex % columns !== 0;
      case 'right':
        return (currentIndex + 1) % columns !== 0 && currentIndex + 1 < totalProducts;
      default:
        return false;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando productos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Productos</h1>
          <p className="mt-2 text-gray-600">
            Gestiona el catálogo de productos de Mundo Carne.
            {categories.length === 0 && (
              <span className="text-red-600 font-medium">
                {' '}Primero crea categorías para poder agregar productos.
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => openModal()}
          disabled={categories.length === 0}
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Producto
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar productos por nombre o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-lg"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Filtrar por Categoría ({filteredProducts.length} productos)
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === ''
                    ? 'bg-red-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                🏷️ Todas las categorías ({products.length})
              </button>
              {categories.map((category) => {
                const categoryProducts = products.filter(p => p.category_id === category.id).length;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name} ({categoryProducts})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="relative">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-48 object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  onClick={() => toggleVisibility(product)}
                  className={`p-1.5 rounded-full ${
                    product.is_visible ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                  }`}
                  title={product.is_visible ? 'Ocultar producto' : 'Mostrar producto'}
                >
                  {product.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
              {product.is_premium && (
                <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                  Premium
                </div>
              )}
            </div>

            <div className="p-6">
              <div className="mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>

              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {product.categories?.name}
                </span>

                <div className="flex gap-1">
                  {/* Flechas de dirección */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveProduct(product.id, 'up')}
                      disabled={!canMoveProduct(product.id, 'up')}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Mover arriba"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveProduct(product.id, 'left')}
                        disabled={!canMoveProduct(product.id, 'left')}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Mover izquierda"
                      >
                        <ChevronLeft className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveProduct(product.id, 'right')}
                        disabled={!canMoveProduct(product.id, 'right')}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        title="Mover derecha"
                      >
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => moveProduct(product.id, 'down')}
                      disabled={!canMoveProduct(product.id, 'down')}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Mover abajo"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => openModal(product)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar producto"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar producto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || selectedCategory ? 'No se encontraron productos' : 'No hay productos registrados'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || selectedCategory
              ? 'Intenta ajustar los filtros de búsqueda o crea un nuevo producto'
              : 'Comienza creando tu primer producto'
            }
          </p>
          {(searchTerm || selectedCategory) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
              }}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría *
                  {categories.length === 0 && (
                    <span className="text-red-500 text-xs ml-2">
                      (Primero crea categorías en la sección de Categorías)
                    </span>
                  )}
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className={`w-full px-3 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-base ${
                    categories.length === 0
                      ? 'border-red-300 bg-red-50 text-red-700'
                      : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">
                    {categories.length === 0 ? 'No hay categorías disponibles' : 'Seleccionar categoría'}
                  </option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                {categories.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Los productos se organizan por categorías para facilitar la navegación
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Imagen Principal *</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="https://ejemplo.com/imagen.jpg"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imagen 2 (opcional)</label>
                  <input
                    type="url"
                    value={formData.image_url2}
                    onChange={(e) => setFormData({ ...formData, image_url2: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imagen 3 (opcional)</label>
                  <input
                    type="url"
                    value={formData.image_url3}
                    onChange={(e) => setFormData({ ...formData, image_url3: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imagen 4 (opcional)</label>
                  <input
                    type="url"
                    value={formData.image_url4}
                    onChange={(e) => setFormData({ ...formData, image_url4: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="https://ejemplo.com/imagen.jpg"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_premium}
                    onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Producto Premium</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_visible}
                    onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Visible en el sitio</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Guardando...' : (editingProduct ? 'Actualizar' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;